import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { makeStyles } from "tss-react/mui";
import {
  Autocomplete,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  InputBase,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import CloudOffOutlinedIcon from "@mui/icons-material/CloudOffOutlined";
import CloudDoneOutlinedIcon from "@mui/icons-material/CloudDoneOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import DoneAllOutlinedIcon from "@mui/icons-material/DoneAllOutlined";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "../../services/toast";
import { i18n } from "../../translate/i18n";
import ManualKnowledgeDialog from "./ManualKnowledgeDialog";
import {
  isManualDocument,
  progressLabel,
  REVIEW_STATUSES,
  STATUS_COLORS,
  alertLabel,
  allCountries,
  countryName,
  formatPath,
  parsePath,
} from "./knowledgeHelpers";

const POLL_MS = 4000;
const ALL = "all";

const useStyles = makeStyles()((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  headerInfo: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  meta: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  panel: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  panelTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    userSelect: "none",
  },
  panelList: {
    margin: theme.spacing(1, 0, 0),
    paddingLeft: theme.spacing(3),
    fontSize: 13,
    "& li": { marginBottom: 4 },
  },
  editTitle: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  editHelp: {
    fontSize: 13,
    fontWeight: 400,
    color: theme.palette.text.secondary,
  },
  editContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    paddingTop: theme.spacing(2),
    background: theme.palette.mode === "dark" ? "rgba(0,0,0,0.12)" : theme.palette.grey[50],
  },
  imageNotice: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontSize: 13,
    color: theme.palette.warning.main,
    background: `${theme.palette.warning.main}14`,
    borderRadius: 8,
    padding: theme.spacing(1, 1.5),
  },
  ruleCard: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(1.25, 1.5, 1.5),
    transition: "border-color .15s ease",
    "&:focus-within": { borderColor: theme.palette.primary.main },
  },
  ruleCardHeader: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  ruleBadge: {
    width: 22,
    height: 22,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11.5,
    fontWeight: 700,
    background: theme.palette.action.selected,
  },
  rulePage: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  ruleAlert: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12,
    color: theme.palette.warning.main,
  },
  ruleDelete: {
    color: theme.palette.text.disabled,
    "&:hover": { color: theme.palette.error.main },
  },
  ruleCompare: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) 20px minmax(0, 1fr)",
    gap: theme.spacing(1.5),
    alignItems: "start",
  },
  sideLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: theme.palette.text.secondary,
    marginBottom: 4,
  },
  ruleSource: {
    fontSize: 13,
    lineHeight: 1.55,
    color: theme.palette.text.secondary,
    whiteSpace: "pre-wrap",
    padding: theme.spacing(0.75, 0),
  },
  ruleArrow: {
    marginTop: 26,
    fontSize: 18,
    color: theme.palette.text.disabled,
  },
  ruleTarget: {
    background: `${theme.palette.primary.main}0f`,
    borderRadius: 8,
    padding: theme.spacing(0.75, 1.25),
  },
  ruleInput: {
    fontSize: 14,
    lineHeight: 1.55,
    padding: 0,
  },
  organizationBlock: {
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  editHeader: {
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "flex-start",
    marginTop: theme.spacing(2),
  },
  publishedBanner: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.success.main}`,
    marginBottom: theme.spacing(2),
    fontSize: 13,
  },
  infoCard: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
  },
  fileIcon: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.palette.action.hover,
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
  fileName: {
    fontSize: 15,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  tabs: {
    minHeight: 40,
    borderBottom: `1px solid ${theme.palette.divider}`,
    marginBottom: theme.spacing(1.5),
    "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 600 },
  },
  filters: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
  },
  filter: {
    width: 200,
  },
  search: {
    flex: 1,
    minWidth: 220,
  },
  rowPending: { boxShadow: `inset 3px 0 0 ${theme.palette.divider}` },
  rowApproved: { boxShadow: `inset 3px 0 0 ${theme.palette.success.main}` },
  rowRejected: { boxShadow: `inset 3px 0 0 ${theme.palette.error.main}` },
  alertCount: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    fontSize: 12.5,
    fontWeight: 600,
    color: theme.palette.warning.main,
  },
  muted: {
    color: theme.palette.text.disabled,
  },
  table: {
    tableLayout: "fixed",
  },
  clickable: {
    cursor: "pointer",
  },
  preview: {
    fontSize: 13,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  itemsBox: {
    padding: theme.spacing(1, 1, 2, 5),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.25),
  },
  item: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "flex-start",
  },
  itemText: {
    fontSize: 13.5,
  },
  quote: {
    fontSize: 12,
    fontFamily: "monospace",
    color: theme.palette.text.secondary,
    background: theme.palette.action.hover,
    borderRadius: 6,
    padding: theme.spacing(0.5, 1),
    marginTop: 4,
    whiteSpace: "pre-wrap",
  },
  alertChips: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
    marginTop: 4,
  },
  actions: {
    whiteSpace: "nowrap",
  },
}));

const EntryEditDialog = ({ entry, countries, manual, onClose, onSaved }) => {
  const { classes } = useStyles();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!entry) return;
    setForm({
      path: formatPath(entry.path),
      countryCode: entry.countryCode,
      tags: entry.tags || [],
      items: entry.items.map((item, index) => ({ index, text: item.text })),
    });
  }, [entry]);

  if (!entry || !form) return null;

  const updateItem = (index, text) =>
    setForm((prev) => ({ ...prev, items: prev.items.map((item) => (item.index === index ? { ...item, text } : item)) }));

  const removeItem = (index) => setForm((prev) => ({ ...prev, items: prev.items.filter((item) => item.index !== index) }));

  const save = async (approve) => {
    setSaving(true);
    try {
      await api.put(`/knowledge/${entry.documentId}/entries/${entry.id}`, {
        path: parsePath(form.path),
        countryCode: form.countryCode,
        tags: form.tags,
        items: form.items,
      });
      if (approve) await api.post(`/knowledge/${entry.documentId}/entries/${entry.id}/review`, { status: "approved" });
      toast.success(i18n.t(approve ? "knowledge.entrySavedApproved" : "knowledge.entrySaved"));
      onSaved();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const selectedCountry = countries.find((country) => country.code === form.countryCode) || null;
  const invalid = !parsePath(form.path).length || !form.items.length || form.items.some((item) => !item.text.trim());
  const readFromImages = entry.items.some((item) => item.source === "image");

  return (
    <Dialog open onClose={() => !saving && onClose()} maxWidth="lg" fullWidth>
      <DialogTitle className={classes.editTitle}>
        <span>{i18n.t("knowledge.editTitle", { section: formatPath(entry.path) })}</span>
        <Typography className={classes.editHelp}>{i18n.t("knowledge.editHelp")}</Typography>
      </DialogTitle>
      <DialogContent dividers className={classes.editContent}>
        {readFromImages && (
          <div className={classes.imageNotice}>
            <ReportProblemOutlinedIcon fontSize="small" />
            {i18n.t("knowledge.imageNotice")}
          </div>
        )}

        {form.items.map((item, position) => {
          const original = entry.items[item.index];
          const extraAlerts = original.alerts.filter((alert) => alert !== "IMAGE_ONLY");
          return (
            <div key={item.index} className={classes.ruleCard}>
              <div className={classes.ruleCardHeader}>
                <span className={classes.ruleBadge}>{position + 1}</span>
                <span className={classes.rulePage}>
                  {i18n.t("knowledge.page")} {original.page}
                </span>
                {extraAlerts.map((alert) => (
                  <span key={alert} className={classes.ruleAlert}>
                    <ReportProblemOutlinedIcon style={{ fontSize: 14 }} />
                    {alertLabel(alert)}
                  </span>
                ))}
                <span style={{ flex: 1 }} />
                <Tooltip title={i18n.t("knowledge.removeRule")}>
                  <span>
                    <IconButton
                      size="small"
                      className={classes.ruleDelete}
                      onClick={() => removeItem(item.index)}
                      disabled={form.items.length === 1}
                    >
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </div>
              <div className={classes.ruleCompare}>
                <div>
                  <div className={classes.sideLabel}>{i18n.t(manual ? "knowledge.manual.inText" : "knowledge.inPdf")}</div>
                  <div className={classes.ruleSource}>{original.quotes.join("\n")}</div>
                </div>
                <ArrowForwardOutlinedIcon className={classes.ruleArrow} />
                <div>
                  <div className={classes.sideLabel}>{i18n.t("knowledge.agentWillKnow")}</div>
                  <div className={classes.ruleTarget}>
                    <InputBase
                      multiline
                      fullWidth
                      value={item.text}
                      onChange={(e) => updateItem(item.index, e.target.value)}
                      className={classes.ruleInput}
                    />
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        <div className={classes.organizationBlock}>
          <Typography className={classes.sideLabel}>{i18n.t("knowledge.organization")}</Typography>
          <div className={classes.editHeader}>
            <TextField
              size="small"
              label={i18n.t("knowledge.columns.path")}
              placeholder={i18n.t("knowledge.pathPlaceholder")}
              value={form.path}
              onChange={(e) => setForm((prev) => ({ ...prev, path: e.target.value }))}
              style={{ flex: 2 }}
            />
            <Autocomplete
              options={countries}
              value={selectedCountry}
              onChange={(_, value) => setForm((prev) => ({ ...prev, countryCode: value?.code || null }))}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.code === value.code}
              renderInput={(params) => (
                <TextField {...params} size="small" label={i18n.t("knowledge.columns.country")} placeholder={i18n.t("knowledge.optional")} />
              )}
              style={{ flex: 1 }}
            />
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={form.tags}
              onChange={(_, value) => setForm((prev) => ({ ...prev, tags: value.map((tag) => String(tag).trim()).filter(Boolean) }))}
              renderInput={(params) => (
                <TextField {...params} size="small" label={i18n.t("knowledge.columns.tags")} placeholder={i18n.t("knowledge.tagsPlaceholder")} />
              )}
              style={{ flex: 2 }}
            />
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {i18n.t("knowledge.cancel")}
        </Button>
        <Button variant="outlined" onClick={() => save(false)} disabled={saving || invalid}>
          {i18n.t("knowledge.save")}
        </Button>
        {entry.status !== "approved" && (
          <Button variant="contained" startIcon={<CheckOutlinedIcon />} onClick={() => save(true)} disabled={saving || invalid}>
            {i18n.t("knowledge.saveAndApprove")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const PublishDialog = ({ document, approvedRules, onClose, onPublished }) => {
  const [publishing, setPublishing] = useState(false);

  const publish = async () => {
    setPublishing(true);
    try {
      await api.post(`/knowledge/${document.id}/publish`);
      toast.success(i18n.t("knowledge.publish.done", { count: approvedRules }));
      onPublished();
    } catch (err) {
      toastError(err);
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Dialog open onClose={() => !publishing && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n.t("knowledge.publish.title")}</DialogTitle>
      <DialogContent style={{ display: "flex", flexDirection: "column", gap: 12, paddingTop: 8 }}>
        <Typography variant="body2">{i18n.t("knowledge.publish.summary", { count: approvedRules })}</Typography>
        {document.status === "published" && (
          <Typography variant="body2" color="text.secondary">
            {i18n.t("knowledge.publish.replaceNotice")}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={publishing}>
          {i18n.t("knowledge.cancel")}
        </Button>
        <Button
          variant="contained"
          startIcon={<CloudUploadOutlinedIcon />}
          onClick={publish}
          disabled={publishing || !approvedRules}
        >
          {publishing ? i18n.t("knowledge.publish.publishing") : i18n.t("knowledge.publish.confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ReportPanel = ({ classes, title, count, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  if (!count) return null;
  return (
    <div className={classes.panel}>
      <div className={classes.panelTitle} onClick={() => setOpen((value) => !value)}>
        {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
        <ReportProblemOutlinedIcon fontSize="small" color="warning" />
        {title} ({count})
      </div>
      <Collapse in={open}>{children}</Collapse>
    </div>
  );
};

const KnowledgeReview = () => {
  const { classes } = useStyles();
  const { documentId } = useParams();
  const navigate = useNavigate();
  const [document, setDocument] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [filters, setFilters] = useState({ section: ALL, country: ALL, status: ALL, onlyAlerts: false, search: "" });
  const countries = useMemo(() => allCountries(), []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/knowledge/${documentId}`);
      setDocument(data);
    } catch (err) {
      toastError(err);
    }
  }, [documentId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (document?.status !== "processing") return undefined;
    const timer = setInterval(load, POLL_MS);
    return () => clearInterval(timer);
  }, [document?.status, load]);

  const entries = useMemo(() => document?.entries || [], [document]);

  const sectionOptions = useMemo(() => Array.from(new Set(entries.map((entry) => entry.path?.[0] || ""))), [entries]);

  const countryOptions = useMemo(
    () =>
      Array.from(new Set(entries.map((entry) => entry.countryCode).filter(Boolean))).sort((a, b) =>
        countryName(a).localeCompare(countryName(b))
      ),
    [entries]
  );
  const hasCountries = countryOptions.length > 0;
  const columnCount = hasCountries ? 9 : 8;

  const counts = useMemo(() => {
    const result = { total: entries.length, withAlerts: 0, pending: 0, approved: 0, rejected: 0, cleanPending: 0, approvedRules: 0 };
    entries.forEach((entry) => {
      result[entry.status] += 1;
      if (entry.alerts.length) result.withAlerts += 1;
      if (entry.status === "pending" && !entry.alerts.length) result.cleanPending += 1;
      if (entry.status === "approved") result.approvedRules += entry.items.length;
    });
    return result;
  }, [entries]);

  const unpublish = async () => {
    setBusy(true);
    try {
      await api.post(`/knowledge/${documentId}/unpublish`);
      toast.success(i18n.t("knowledge.publish.unpublished"));
      await load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const visible = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    return entries.filter((entry) => {
      if (filters.section !== ALL && (entry.path?.[0] || "") !== filters.section) return false;
      if (filters.country !== ALL && (entry.countryCode || "") !== filters.country) return false;
      if (filters.status !== ALL && entry.status !== filters.status) return false;
      if (filters.onlyAlerts && !entry.alerts.length) return false;
      if (search) {
        const haystack = `${formatPath(entry.path)} ${(entry.tags || []).join(" ")} ${entry.items.map((item) => item.text).join(" ")}`.toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      return true;
    });
  }, [entries, filters]);

  const replaceEntry = (updated) =>
    setDocument((prev) => ({
      ...prev,
      entries: prev.entries.map((entry) => (entry.id === updated.id ? updated : entry)),
    }));

  const review = async (entry, status) => {
    try {
      const { data } = await api.post(`/knowledge/${entry.documentId}/entries/${entry.id}/review`, { status });
      replaceEntry(data);
    } catch (err) {
      toastError(err);
    }
  };

  const approveClean = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/knowledge/${documentId}/entries/approve-clean`);
      toast.success(i18n.t("knowledge.approvedClean", { count: data.approved }));
      await load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const reprocess = async () => {
    setBusy(true);
    try {
      await api.post(`/knowledge/${documentId}/reprocess`);
      await load();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  const report = document?.report;
  const manual = isManualDocument(document);
  const isReviewable = document?.status === "review" || document?.status === "published";

  if (!document) {
    return (
      <MainContainer maxWidth={false}>
        <LinearProgress />
      </MainContainer>
    );
  }

  return (
    <MainContainer maxWidth={false}>
      {publishOpen && (
        <PublishDialog
          document={document}
          approvedRules={counts.approvedRules}
          onClose={() => setPublishOpen(false)}
          onPublished={() => {
            setPublishOpen(false);
            load();
          }}
        />
      )}
      {editing && (
        <EntryEditDialog
          entry={editing}
          countries={countries}
          manual={manual}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {manual && (
        <ManualKnowledgeDialog
          open={manualOpen}
          lockName
          initialName={document.name}
          initialText={document.manualText || ""}
          onClose={() => setManualOpen(false)}
          onSaved={(saved) => {
            toast.success(i18n.t("knowledge.manual.saved"));
            setManualOpen(false);
            navigate(`/knowledge/${saved.id}`);
          }}
        />
      )}

      <MainHeader>
        <Title>
          <IconButton size="small" onClick={() => navigate("/knowledge")} style={{ marginRight: 8 }}>
            <ArrowBackOutlinedIcon fontSize="small" />
          </IconButton>
          {document.name} · v{document.version}
        </Title>
        <MainHeaderButtonsWrapper>
          {manual && document.status !== "processing" && (
            <Button variant="outlined" startIcon={<EditNoteOutlinedIcon />} onClick={() => setManualOpen(true)} disabled={busy}>
              {i18n.t("knowledge.manual.editText")}
            </Button>
          )}
          {isReviewable && counts.cleanPending > 0 && (
            <Button variant="outlined" startIcon={<DoneAllOutlinedIcon />} onClick={approveClean} disabled={busy}>
              {i18n.t("knowledge.approveClean", { count: counts.cleanPending })}
            </Button>
          )}
          {document.status === "published" && (
            <Button variant="outlined" color="error" startIcon={<CloudOffOutlinedIcon />} onClick={unpublish} disabled={busy}>
              {i18n.t("knowledge.publish.unpublish")}
            </Button>
          )}
          {isReviewable && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<CloudUploadOutlinedIcon />}
              onClick={() => setPublishOpen(true)}
              disabled={busy || !counts.approved}
            >
              {document.status === "published"
                ? i18n.t("knowledge.publish.republish")
                : i18n.t("knowledge.publish.button", { count: counts.approvedRules })}
            </Button>
          )}
          {document.status === "failed" && (
            <Button variant="contained" startIcon={<ReplayOutlinedIcon />} onClick={reprocess} disabled={busy}>
              {i18n.t("knowledge.reprocess")}
            </Button>
          )}
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <div className={classes.infoCard}>
          <div className={classes.fileIcon}>
            {manual ? <EditNoteOutlinedIcon /> : <PictureAsPdfOutlinedIcon />}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Typography className={classes.fileName} noWrap title={document.originalName}>
              {manual ? i18n.t("knowledge.manual.source") : document.originalName}
            </Typography>
            <Typography className={classes.meta} noWrap>
              {[
                !manual && document.pageCount ? i18n.t("knowledge.pagesCount", { count: document.pageCount }) : null,
                document.usage?.costUsd ? i18n.t("knowledge.costLabel", { cost: Number(document.usage.costUsd).toFixed(2) }) : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          </div>
          <Chip
            size="small"
            color={STATUS_COLORS[document.status] || "default"}
            variant="outlined"
            label={i18n.t(`knowledge.documentStatus.${document.status}`)}
          />
        </div>

        {document.status === "processing" && (
          <div className={classes.panel}>
            <LinearProgress
              variant={document.progress?.total > 1 ? "determinate" : "indeterminate"}
              value={document.progress?.total ? (document.progress.done / document.progress.total) * 100 : 0}
            />
            <Typography className={classes.meta} style={{ marginTop: 8 }}>
              {progressLabel(document.progress)}
            </Typography>
          </div>
        )}

        {document.status === "failed" && (
          <div className={classes.panel}>
            <Typography color="error" variant="body2">
              {i18n.exists(`backendErrors.${document.error}`) ? i18n.t(`backendErrors.${document.error}`) : document.error}
            </Typography>
          </div>
        )}

        {document.status === "published" && (
          <div className={classes.publishedBanner}>
            <CloudDoneOutlinedIcon color="success" fontSize="small" />
            <span>
              {i18n.t("knowledge.publish.banner", {
                count: document.publishedCount || 0,
                date: document.publishedAt ? new Date(document.publishedAt).toLocaleString() : "",
              })}
            </span>
            {document.hasUnpublishedChanges && (
              <Chip size="small" color="warning" variant="outlined" label={i18n.t("knowledge.publish.pendingChanges")} />
            )}
          </div>
        )}

        {isReviewable && (
          <>
            <ReportPanel classes={classes} title={i18n.t("knowledge.report.inconsistencies")} count={report?.inconsistencies?.length} defaultOpen>
              <ul className={classes.panelList}>
                {report?.inconsistencies?.map((inconsistency, index) => (
                  <li key={index}>
                    {inconsistency.description}
                    {inconsistency.pages?.length ? ` (${i18n.t("knowledge.page")} ${inconsistency.pages.join(", ")})` : ""}
                  </li>
                ))}
              </ul>
            </ReportPanel>

            <ReportPanel classes={classes} title={i18n.t("knowledge.report.warnings")} count={report?.warnings?.length}>
              <ul className={classes.panelList}>
                {report?.warnings?.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </ReportPanel>

            <Tabs
              value={filters.status}
              onChange={(_, value) => setFilters((prev) => ({ ...prev, status: value }))}
              className={classes.tabs}
            >
              <Tab value={ALL} label={`${i18n.t("knowledge.filters.all")} (${counts.total})`} />
              {REVIEW_STATUSES.map((status) => (
                <Tab key={status} value={status} label={`${i18n.t(`knowledge.statusTabs.${status}`)} (${counts[status]})`} />
              ))}
            </Tabs>

            <div className={classes.filters}>
              <TextField
                select
                size="small"
                className={classes.filter}
                label={i18n.t("knowledge.columns.section")}
                value={filters.section}
                onChange={(e) => setFilters((prev) => ({ ...prev, section: e.target.value }))}
              >
                <MenuItem value={ALL}>{i18n.t("knowledge.filters.all")}</MenuItem>
                {sectionOptions.map((section) => (
                  <MenuItem key={section || "none"} value={section}>
                    {section || "—"}
                  </MenuItem>
                ))}
              </TextField>
              {hasCountries && (
                <TextField
                  select
                  size="small"
                  className={classes.filter}
                  label={i18n.t("knowledge.columns.country")}
                  value={filters.country}
                  onChange={(e) => setFilters((prev) => ({ ...prev, country: e.target.value }))}
                >
                  <MenuItem value={ALL}>{i18n.t("knowledge.filters.all")}</MenuItem>
                  {countryOptions.map((code) => (
                    <MenuItem key={code} value={code}>
                      {countryName(code)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
              <TextField
                size="small"
                className={classes.search}
                placeholder={i18n.t("knowledge.filters.search")}
                value={filters.search}
                onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchOutlinedIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              {counts.withAlerts > 0 && (
                <Button
                  variant={filters.onlyAlerts ? "contained" : "outlined"}
                  color="warning"
                  startIcon={<ReportProblemOutlinedIcon fontSize="small" />}
                  onClick={() => setFilters((prev) => ({ ...prev, onlyAlerts: !prev.onlyAlerts }))}
                  style={{ height: 40, textTransform: "none" }}
                >
                  {i18n.t("knowledge.filters.onlyAlertsCount", { count: counts.withAlerts })}
                </Button>
              )}
            </div>

            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell width={36} />
                  <TableCell width={260}>{i18n.t("knowledge.columns.path")}</TableCell>
                  {hasCountries && <TableCell width={130}>{i18n.t("knowledge.columns.country")}</TableCell>}
                  <TableCell width={180}>{i18n.t("knowledge.columns.tags")}</TableCell>
                  <TableCell>{i18n.t("knowledge.columns.content")}</TableCell>
                  <TableCell width={80} align="center">{i18n.t("knowledge.columns.pages")}</TableCell>
                  <TableCell width={90} align="center">{i18n.t("knowledge.columns.alerts")}</TableCell>
                  <TableCell width={110}>{i18n.t("knowledge.columns.status")}</TableCell>
                  <TableCell width={120} align="center">{i18n.t("knowledge.columns.actions")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visible.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columnCount} align="center">
                      {i18n.t("knowledge.noEntries")}
                    </TableCell>
                  </TableRow>
                )}
                {visible.map((entry) => {
                  const open = Boolean(expanded[entry.id]);
                  const alertItems = entry.items.filter((item) => item.alerts.length).length;
                  const rowAccent =
                    entry.status === "approved" ? classes.rowApproved : entry.status === "rejected" ? classes.rowRejected : classes.rowPending;
                  return (
                    <Fragment key={entry.id}>
                      <TableRow hover className={rowAccent}>
                        <TableCell
                          className={classes.clickable}
                          onClick={() => setExpanded((prev) => ({ ...prev, [entry.id]: !prev[entry.id] }))}
                        >
                          {open ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" className={classes.preview} title={formatPath(entry.path)}>
                            {formatPath(entry.path)}
                          </Typography>
                        </TableCell>
                        {hasCountries && (
                          <TableCell>
                            <Typography variant="body2" noWrap>
                              {entry.countryCode ? countryName(entry.countryCode) : "—"}
                            </Typography>
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="body2" className={classes.preview} title={(entry.tags || []).join(", ")}>
                            {(entry.tags || []).join(", ") || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell
                          className={classes.clickable}
                          onClick={() => setExpanded((prev) => ({ ...prev, [entry.id]: !prev[entry.id] }))}
                        >
                          <div className={classes.preview}>
                            {i18n.t("knowledge.rulesCount", { count: entry.items.length })}:{" "}
                            {entry.items.map((item) => item.text).join(" · ")}
                          </div>
                        </TableCell>
                        <TableCell align="center">{entry.pages.join(", ")}</TableCell>
                        <TableCell align="center">
                          {entry.alerts.length ? (
                            <Tooltip title={entry.alerts.map(alertLabel).join(" · ")}>
                              <span className={classes.alertCount}>
                                <ReportProblemOutlinedIcon style={{ fontSize: 16 }} />
                                {alertItems}
                              </span>
                            </Tooltip>
                          ) : (
                            <span className={classes.muted}>—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            variant="outlined"
                            color={STATUS_COLORS[entry.status] || "default"}
                            label={i18n.t(`knowledge.entryStatus.${entry.status}`)}
                          />
                        </TableCell>
                        <TableCell align="center" className={classes.actions}>
                          {entry.status === "pending" ? (
                            <>
                              <Tooltip title={i18n.t("knowledge.approve")}>
                                <IconButton size="small" color="success" onClick={() => review(entry, "approved")}>
                                  <CheckOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={i18n.t("knowledge.reject")}>
                                <IconButton size="small" color="error" onClick={() => review(entry, "rejected")}>
                                  <CloseOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          ) : (
                            <Tooltip title={i18n.t("knowledge.undoReview")}>
                              <IconButton size="small" onClick={() => review(entry, "pending")}>
                                <UndoOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title={i18n.t("knowledge.edit")}>
                            <IconButton size="small" onClick={() => setEditing(entry)}>
                              <EditOutlinedIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell colSpan={columnCount} style={{ padding: 0, borderBottom: open ? undefined : "none" }}>
                          <Collapse in={open} unmountOnExit>
                            <div className={classes.itemsBox}>
                              {entry.items.map((item, index) => (
                                <div key={index} className={classes.item}>
                                  {item.verified ? (
                                    <Tooltip title={i18n.t("knowledge.verified")}>
                                      <VerifiedOutlinedIcon fontSize="small" color="success" />
                                    </Tooltip>
                                  ) : (
                                    <Tooltip title={i18n.t("knowledge.notVerified")}>
                                      <ReportProblemOutlinedIcon fontSize="small" color="warning" />
                                    </Tooltip>
                                  )}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div className={classes.itemText}>{item.text}</div>
                                    <div className={classes.quote}>
                                      {i18n.t("knowledge.page")} {item.page} · {item.quotes.join("  |  ")}
                                    </div>
                                    {item.alerts.length > 0 && (
                                      <div className={classes.alertChips}>
                                        {item.alerts.map((alert) => (
                                          <Chip key={alert} size="small" color="warning" variant="outlined" label={alertLabel(alert)} />
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </>
        )}
      </Paper>
    </MainContainer>
  );
};

export default KnowledgeReview;
